import React, { useState } from "react";

import {
  Table,
  Tag,
  Image,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Upload,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const STATUS_COLOR = {
  Available: "green",
  "On Ride": "blue",
  Inactive: "red",
};

const API_BASE =
  "https://prashantbha.pythonanywhere.com/api";

const Cabs = () => {

  const [data, setData] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [editingCab, setEditingCab] =
    useState(null);

  const [imageFile, setImageFile] =
    useState(null);

  const [form] = Form.useForm();

  // =========================
  // FETCH CABS
  // =========================

  const fetchCabs = async () => {

    try {

      setLoading(true);

      const res = await fetch(
        `${API_BASE}/cabs/`
      );

      const result = await res.json();

      setData(result);

    } catch (err) {

      console.log(err);

      message.error(
        "Failed to load cabs"
      );

    } finally {

      setLoading(false);
    }
  };

  React.useEffect(() => {

    fetchCabs();

  }, []);

  // =========================
  // ADD
  // =========================

  const handleAdd = () => {

    setEditingCab(null);

    setImageFile(null);

    form.resetFields();

    setIsModalOpen(true);
  };

  // =========================
  // EDIT
  // =========================

  const handleEdit = (record) => {

    setEditingCab(record);

    setImageFile(record.img);

    form.setFieldsValue(record);

    setIsModalOpen(true);
  };

  // =========================
  // DELETE
  // =========================

  const handleDelete = async (id) => {

    try {

      await fetch(
        `${API_BASE}/cabs/${id}/delete/`,
        {
          method: "DELETE",
        }
      );

      message.success(
        "Cab deleted"
      );

      fetchCabs();

    } catch (err) {

      console.log(err);

      message.error(
        "Delete failed"
      );
    }
  };

  // =========================
  // SAVE
  // =========================

  const handleOk = async () => {

    try {

      const values =
        await form.validateFields();

      const payload = {

        vehicle: values.vehicle,

        type: values.type,

        fuel: values.fuel,

        img: imageFile,

        price: Number(values.price),

        pricePerKm: Number(
          values.pricePerKm
        ),

        rating: Number(
          values.rating
        ),

        status: values.status,
      };

      // =====================
      // UPDATE
      // =====================

      if (editingCab) {

        await fetch(
          `${API_BASE}/cabs/${editingCab.key}/update/`,
          {

            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify(
              payload
            ),
          }
        );

        message.success(
          "Cab updated"
        );
      }

      // =====================
      // CREATE
      // =====================

      else {

        await fetch(
          `${API_BASE}/cabs/create/`,
          {

            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify(
              payload
            ),
          }
        );

        message.success(
          "Cab added"
        );
      }

      setIsModalOpen(false);

      fetchCabs();

    } catch (err) {

      console.log(err);

      message.error(
        "Something went wrong"
      );
    }
  };

  // =========================
  // TABLE COLUMNS
  // =========================

  const columns = [

    {
      title: "Car Photo",

      key: "photo",

      render: (r) => (

        <Image
          src={r.img}

          width={90}

          height={64}

          style={{
            objectFit: "contain",
            background: "#f8fafc",
            borderRadius: 8,
            padding: 4,
          }}

          preview={false}
        />
      ),

      width: 110,
    },

    {
      title: "Vehicle",

      dataIndex: "vehicle",

      key: "vehicle",

      render: (v) => (

        <div
          style={{
            fontWeight: 700,
            color: "#1a1a2e",
          }}
        >
          {v}
        </div>
      ),
    },

    {
      title: "Type",

      dataIndex: "type",

      key: "type",

      render: (t) => (
        <Tag color="blue">
          {t}
        </Tag>
      ),

      filters: [
        {
          text: "Sedan",
          value: "sedan",
        },

        {
          text: "SUV",
          value: "suv",
        },

        {
          text: "Hatchback",
          value: "hatchback",
        },

        {
          text: "Premium SUV",
          value: "premium_suv",
        },
      ],

      onFilter: (v, r) =>
        r.type === v,
    },

    {
      title: "Fuel",

      dataIndex: "fuel",

      key: "fuel",
    },

    {
      title: "Pricing",

      key: "price",

      render: (r) => (

        <div>

          <b
            style={{
              color: "#d4a80f",
            }}
          >
            ₹
            {Number(
              r.price
            ).toLocaleString()}
          </b>

          <div
            style={{
              fontSize: 12,
              color: "#9ca3af",
            }}
          >
            ₹
            {r.pricePerKm}
            /km
          </div>

        </div>
      ),

      sorter: (a, b) =>
        a.price - b.price,
    },

    {
      title: "Rating",

      key: "rating",

      render: (r) => (

        <span
          style={{
            fontWeight: 700,
            color: "#d4a80f",
          }}
        >
          ★ {r.rating}
        </span>
      ),

      sorter: (a, b) =>
        a.rating - b.rating,
    },

    {
      title: "Status",

      dataIndex: "status",

      key: "status",

      render: (s) => (

        <Tag
          color={
            STATUS_COLOR[s] ||
            "default"
          }
        >
          {s}
        </Tag>
      ),

      filters: [

        {
          text: "Available",
          value: "Available",
        },

        {
          text: "On Ride",
          value: "On Ride",
        },

        {
          text: "Inactive",
          value: "Inactive",
        },
      ],

      onFilter: (v, r) =>
        r.status === v,
    },

    {
      title: "Action",

      key: "action",

      render: (record) => (

        <Space>

          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}

            onClick={() =>
              handleEdit(record)
            }
          >
            Edit
          </Button>

          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}

            onClick={() =>
              handleDelete(
                record.key
              )
            }
          >
            Delete
          </Button>

        </Space>
      ),
    },
  ];

  return (

    <div
      style={{
        fontFamily:
          "'Plus Jakarta Sans',sans-serif",
      }}
    >

      {/* ===================== */}
      {/* HEADER */}
      {/* ===================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >

        <div>

          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#1a1a2e",
              margin: 0,
            }}
          >
            Cabs
          </h2>

          <p
            style={{
              color: "#9ca3af",
              fontSize: 13,
              marginTop: 2,
            }}
          >

            {
              data.filter(
                (c) =>
                  c.status ===
                  "Available"
              ).length
            }

            {" "}available ·{" "}

            {
              data.filter(
                (c) =>
                  c.status ===
                  "On Ride"
              ).length
            }

            {" "}on ride

          </p>

        </div>

        <Button
          type="primary"

          icon={<PlusOutlined />}

          onClick={handleAdd}

          style={{
            background:
              "linear-gradient(135deg,#f5c518,#d4a80f)",

            border: "none",

            color: "#1a1a2e",

            fontWeight: 700,

            borderRadius: 8,

            height: 38,
          }}
        >
          + Add Cab
        </Button>

      </div>

      {/* ===================== */}
      {/* TABLE */}
      {/* ===================== */}

      <Table
        dataSource={data}

        columns={columns}

        rowKey="key"

        loading={loading}

        scroll={{ x: true }}

        pagination={{
          pageSize: 8,
        }}

        style={{
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
        }}
      />

      {/* ===================== */}
      {/* MODAL */}
      {/* ===================== */}

      <Modal

        title={

          <span
            style={{
              fontWeight: 700,
            }}
          >
            {
              editingCab
                ? "✏️ Edit Cab"
                : "➕ Add New Cab"
            }
          </span>
        }

        open={isModalOpen}

        onOk={handleOk}

        onCancel={() =>
          setIsModalOpen(false)
        }

        okText="Save"

        width={520}

        okButtonProps={{
          style: {
            background:
              "#f5c518",

            borderColor:
              "#f5c518",

            color: "#1a1a2e",

            fontWeight: 700,
          },
        }}
      >

        <Form
          form={form}

          layout="vertical"

          style={{
            marginTop: 16,
          }}
        >

          <Form.Item
            name="vehicle"

            label="Vehicle Name"

            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="type"

            label="Vehicle Type"

            rules={[
              {
                required: true,
              },
            ]}
          >

            <Select>

              <Option value="hatchback">
                Hatchback
              </Option>

              <Option value="sedan">
                Sedan
              </Option>

              <Option value="suv">
                SUV
              </Option>

              <Option value="premium_suv">
                Premium SUV
              </Option>

            </Select>

          </Form.Item>

          <Form.Item
            name="fuel"
            label="Fuel Type"
          >

            <Select>

              <Option value="Petrol">
                Petrol
              </Option>

              <Option value="Diesel">
                Diesel
              </Option>

              <Option value="CNG">
                CNG
              </Option>

            </Select>

          </Form.Item>

          {/* IMAGE UPLOAD */}

          <Form.Item
            label="Vehicle Image"
          >

            <Upload

              beforeUpload={(file) => {

                const reader =
                  new FileReader();

                reader.onload = (
                  e
                ) => {

                  setImageFile(
                    e.target.result
                  );

                  form.setFieldsValue({
                    img:
                      e.target.result,
                  });
                };

                reader.readAsDataURL(
                  file
                );

                return false;
              }}

              showUploadList={false}
            >

              <Button
                icon={
                  <UploadOutlined />
                }
              >
                Upload Image
              </Button>

            </Upload>

            {imageFile && (

              <div
                style={{
                  marginTop: 14,
                }}
              >

                <img
                  src={imageFile}

                  alt="preview"

                  style={{
                    width: 140,
                    height: 90,
                    objectFit:
                      "contain",

                    borderRadius: 12,

                    border:
                      "1px solid #e5e7eb",

                    padding: 8,

                    background:
                      "#fff",
                  }}
                />

              </div>
            )}

          </Form.Item>

          <Form.Item
            name="price"

            label="Base Price (₹)"

            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              type="number"
            />
          </Form.Item>

          <Form.Item
            name="pricePerKm"

            label="Price per KM (₹)"

            rules={[
              {
                required: true,
              },
            ]}
          >

            <Input
              type="number"
              step="0.5"
            />

          </Form.Item>

          <Form.Item
            name="rating"
            label="Rating"
          >

            <Input
              type="number"
              step="0.1"
              min={1}
              max={5}
            />

          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
          >

            <Select>

              <Option value="Available">
                Available
              </Option>

              <Option value="On Ride">
                On Ride
              </Option>

              <Option value="Inactive">
                Inactive
              </Option>

            </Select>

          </Form.Item>

        </Form>

      </Modal>

    </div>
  );
};

export default Cabs;